export const ORDER_GROUPS2_PATH = '/order_groups2'

export function wizardStepForReadiness ({ detailsComplete, artworkComplete, stylesComplete }) {
  if (!detailsComplete) return 1
  if (!artworkComplete) return 2
  if (!stylesComplete) return 3
  return 4
}

export function canNavigateToWizardStep (targetStep, activeStep, highestAvailableStep) {
  return targetStep >= 1 && targetStep <= 4 && (
    targetStep <= activeStep || targetStep <= highestAvailableStep
  )
}

export function canAddAnotherDesign (designs, placementIsComplete) {
  if (!designs.length) return true
  const currentDesign = designs[designs.length - 1]
  return (currentDesign.placements || []).some(placementIsComplete)
}

export function sectionsUsingDesign (sections, designId) {
  return (sections || []).reduce((labels, section, index) => {
    if (section.bundle_id === designId) labels.push('Section ' + (index + 1))
    return labels
  }, [])
}

export function designHasEnteredData (design) {
  if (!design) return false
  return !!(design.name || design.notes || (design.placements || []).some(placement => (
    placement.artwork || placement.artwork_name || placement.decoration_text || placement.technique || placement.location
  )))
}

export function clearArtworkDependentState (decoration) {
  if (!decoration) return decoration
  const cleared = {
    artwork: '', artwork_lookup: null, artwork_name: '', artwork_preview_url: '',
    technique: '', location: '', techniqueOptions: [], locationOptions: [],
    original_artwork_technique_key: '', original_artwork_location_key: '',
    artwork_configuration_original: null, artwork_configuration_changed: false, artwork_configuration_editing: false,
    requires_new_art: false, artwork_width: '', artwork_height: '', artwork_units: '',
    tape_id: '', tape_number: '', tape_internal_id: '', artwork_internal_id: '',
    media_id: '', tape_name: '', stitches: '', placement_label: '', license_design_location_id: '',
    proof: null, proof_state: null, proof_url: '', artwork_colors: [], colorway: null,
    artwork_metadata: null, artworkResolving: false
  }
  Object.keys(cleared).forEach(key => { decoration[key] = cleared[key] })
  return decoration
}

export function selectedArtworkMetadata (decoration) {
  const artwork = decoration && decoration.artwork ? decoration.artwork : null
  if (!artwork) return null
  const tape = artwork.tape_info || null
  const filename = artwork.file_name || artwork.name || ''
  const description = artwork.description || (tape && (tape.description || tape.tape_name)) || ''
  const width = tape && validPhysicalDimension(tape.width) ? String(tape.width) : ''
  const height = tape && validPhysicalDimension(tape.height) ? String(tape.height) : ''
  const tapeNumber = artwork.tape_number || artwork.tape_no || artwork.ref || (tape && (tape.tape_number || tape.tape_no || tape.ref)) || ''
  return {
    filename,
    description: description !== filename ? description : '',
    physicalDimensions: width && height ? width + '″ W × ' + height + '″ H' : '',
    tapeNumber: tapeNumber && tapeNumber !== filename ? String(tapeNumber) : '',
    locationHint: decoration.location ? [decoration.location.location_code, decoration.location.location_name].filter(Boolean).join(' — ') : '',
    techniqueHint: decoration.technique ? (decoration.technique.technique_name || decoration.technique.name || '') : ''
  }
}

function validPhysicalDimension (value) {
  return value !== null && value !== undefined && value !== '' && isFinite(Number(value)) && Number(value) > 0
}

export function styleLineIsReady (line, colorTotal) {
  if (!line || !line.style_info || !line.style_info.style_id || (parseInt(line.units || 0, 10) || 0) <= 0) {
    return false
  }
  return (line.colors || []).some(color => colorTotal(color) > 0)
}

export function sectionIsReviewReady (section, setupIsReady, lineIsReady) {
  return !!(
    section &&
    setupIsReady(section) &&
    (section.style_lines || []).length &&
    section.style_lines.every(lineIsReady)
  )
}

export function resolveProgramDecoration ({ techniqueHint, locationHint, techniqueOptions, locationOptions, targetProgramId, targetStyleProductClass, techniqueOptionsLoaded = true, locationOptionsLoaded = true, sourceTechniqueStatus = 'resolved', sourceLocationStatus = 'resolved', onDebug }) {
  const techniqueInternalId = normalizeCanonicalInternalId(techniqueHint && techniqueHint.internal_id)
  const locationInternalId = normalizeCanonicalInternalId(locationHint && locationHint.internal_id)
  const debug = {
    targetProgramId: targetProgramId || null,
    sourceTechnique: debugTechnique(techniqueHint),
    normalizedTechniqueInternalId: techniqueInternalId,
    techniqueOptionsLoaded,
    techniqueCandidates: (techniqueOptions || []).map(debugTechnique),
    matchingTechniqueIds: [],
    techniqueResult: '',
    sourceLocation: debugLocation(locationHint),
    sourceProductClass: locationHint && locationHint.product_class ? locationHint.product_class : null,
    targetStyleProductClass: normalizeCanonicalInternalId(targetStyleProductClass),
    normalizedLocationInternalId: locationInternalId,
    locationOptionsLoaded,
    locationCandidates: (locationOptions || []).map(debugLocation),
    matchingLocationIds: [],
    locationResult: ''
  }
  const finish = result => {
    if (typeof onDebug === 'function') onDebug(debug)
    return result
  }
  if (!techniqueOptionsLoaded) {
    debug.techniqueResult = 'options not loaded'
    debug.locationResult = locationOptionsLoaded ? 'not evaluated' : 'options not loaded'
    return finish({ status: 'target_technique_options_not_loaded', technique: null, location: null })
  }
  if (!['resolved', 'source_identity_not_loaded', 'source_internal_id_available'].includes(sourceTechniqueStatus)) {
    debug.techniqueResult = sourceTechniqueStatus.replace(/_/g, ' ')
    debug.locationResult = 'not evaluated'
    return finish({ status: sourceTechniqueStatus, technique: null, location: null })
  }
  if (techniqueInternalId === null) {
    debug.techniqueResult = 'internal ID missing'
    debug.locationResult = locationOptionsLoaded ? 'not evaluated' : 'options not loaded'
    return finish({ status: 'missing_technique_identity', technique: null, location: null })
  }

  const exactTechniqueId = techniqueHint && techniqueHint.technique_id !== null && techniqueHint.technique_id !== undefined && String(techniqueHint.technique_id).trim()
    ? String(techniqueHint.technique_id)
    : null
  const techniqueMatches = (techniqueOptions || []).filter(option => exactTechniqueId !== null
    ? String(option.technique_id) === exactTechniqueId
    : normalizeCanonicalInternalId(option.internal_id) === techniqueInternalId)
  debug.matchingTechniqueIds = techniqueMatches.map(option => option.technique_id)
  if (techniqueMatches.length !== 1) {
    debug.techniqueResult = techniqueOptionsLoaded
      ? (techniqueMatches.length ? 'multiple matches' : 'no matching candidate')
      : 'options not loaded'
    debug.locationResult = locationOptionsLoaded ? 'not evaluated' : 'options not loaded'
    return finish({
      status: techniqueMatches.length ? 'ambiguous_program_technique' : 'missing_program_technique',
      technique: null,
      location: null
    })
  }
  debug.techniqueResult = 'resolved'

  if (!locationOptionsLoaded) {
    debug.locationResult = 'options not loaded'
    return finish({ status: 'target_location_options_not_loaded', technique: techniqueMatches[0], location: null })
  }
  if (sourceLocationStatus !== 'resolved' && sourceLocationStatus !== 'source_identity_not_loaded') {
    debug.locationResult = sourceLocationStatus.replace(/_/g, ' ')
    return finish({ status: sourceLocationStatus, technique: techniqueMatches[0], location: null })
  }
  if (locationInternalId === null) {
    debug.locationResult = 'internal ID missing'
    return finish({ status: 'missing_location_identity', technique: techniqueMatches[0], location: null })
  }
  let locationMatches = (locationOptions || []).filter(option => (
    normalizeCanonicalInternalId(option.internal_id) === locationInternalId
  ))
  const productClass = normalizeCanonicalInternalId(targetStyleProductClass)
  if (productClass !== null) {
    locationMatches = locationMatches.filter(option => normalizeCanonicalInternalId(option.product_class) === productClass)
  }
  const candidateProductClasses = locationMatches.map(option => normalizeCanonicalInternalId(option.product_class)).filter(Boolean)
  if (locationMatches.length > 1 && productClass === null && candidateProductClasses.length === locationMatches.length) {
    debug.matchingLocationIds = locationMatches.map(option => option.location_id)
    debug.locationResult = 'waiting for style product class'
    return finish({ status: 'waiting_for_style_product_class', technique: techniqueMatches[0], location: null })
  }
  debug.matchingLocationIds = locationMatches.map(option => option.location_id)
  if (locationMatches.length !== 1) {
    debug.locationResult = locationOptionsLoaded
      ? (locationMatches.length ? 'multiple matches' : 'no matching candidate')
      : 'options not loaded'
    return finish({
      status: locationMatches.length ? 'duplicate_location_configuration' : 'location_not_configured',
      technique: techniqueMatches[0],
      location: null
    })
  }
  debug.locationResult = 'resolved'
  return finish({ status: 'resolved', technique: techniqueMatches[0], location: locationMatches[0] })
}

export function normalizeCanonicalInternalId (value) {
  if (value === null || value === undefined) return null
  const normalized = String(value).trim()
  return normalized === '' ? null : normalized
}

export function authoritativeSourceHint (serializedHint, hydration) {
  const saved = serializedHint || null
  if (hydration && hydration.status === 'source_internal_id_available' && hydration.authoritative_internal_id !== null && hydration.authoritative_internal_id !== undefined) {
    return {
      hint: Object.assign({}, saved || {}, {
        internal_id: hydration.authoritative_internal_id,
        serialized_internal_id: hydration.serialized_internal_id,
        hydration_source: 'media.netsuite_design_type_internal_id'
      }),
      status: hydration.status,
      hydration: hydration
    }
  }
  if (!hydration || !['resolved', 'source_canonical_id_unconfigured'].includes(hydration.status) || !hydration.record) {
    return { hint: saved, status: hydration ? hydration.status : 'source_identity_not_loaded', hydration: hydration || null }
  }
  return {
    hint: Object.assign({}, saved || {}, hydration.record, {
      serialized_internal_id: saved && saved.internal_id !== undefined ? saved.internal_id : null,
      hydration_source: (hydration.record.technique_id !== undefined ? 'techniques.technique_id ' : 'locations.location_id ') + hydration.primary_id
    }),
    status: hydration.status,
    hydration: hydration
  }
}

export function configurationChangedSinceSave (savedResolution, currentResolution) {
  if (!savedResolution || !currentResolution || currentResolution.status !== 'resolved') return false
  const savedTechniqueId = savedResolution.technique && savedResolution.technique.technique_id
  const savedLocationId = savedResolution.location && savedResolution.location.location_id
  // Older drafts frequently have partial/default metadata. Only an exact saved
  // tuple can become a user-confirmable change after authoritative hydration.
  if (!savedTechniqueId || !savedLocationId) return false
  return String(savedTechniqueId || '') !== String(currentResolution.technique && currentResolution.technique.technique_id || '') ||
    String(savedLocationId || '') !== String(currentResolution.location && currentResolution.location.location_id || '')
}

export function productClassKeysForStyles (styleLines) {
  return (styleLines || []).map(line => line && line.style_info && line.style_info.product_class)
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
}

export function placementAllowsStyleEditing (status) {
  return [
    'resolved',
    'source_internal_id_available',
    'waiting_for_style_product_class',
    'source_canonical_id_unconfigured',
    'ambiguous_program_technique',
    'missing_program_technique',
    'missing_program_location',
    'ambiguous_program_location',
    'location_not_configured',
    'duplicate_location_configuration'
  ].includes(status)
}

// Design intent may defer Location to a Style. This does not authorize quantity entry.
export function designLocationDeferred (placement) {
  if (!placement) return false
  const technique = placement.technique || placement.source_technique_hint
  return placement.resolution_status === 'waiting_for_style_product_class' && !!(technique && technique.technique_id)
}

export function bundlePlacementsForLine (section, line) {
  if (!section || !section.bundle_id) return []
  if (line && line.style_info && line.style_info.style_id) {
    const resolutions = line.bundle_resolutions || {}
    return resolutions[section.bundle_id] || []
  }
  const resolutions = section.bundle_resolutions || {}
  return resolutions[section.bundle_id] || []
}

export function configurationTupleKey (candidate) {
  if (!candidate) return ''
  return [candidate.program_id, candidate.product_class, candidate.technique_id, candidate.location_id].map(value => String(value || '')).join(':')
}

export function deduplicateConfigurationTuples (candidates) {
  const seen = {}
  return (candidates || []).reduce((result, candidate) => {
    const normalized = Object.assign({}, candidate, {
      configuration_key: candidate.configuration_key || configurationTupleKey(candidate)
    })
    const key = normalized.ptl_identity || normalized.configuration_key
    if (!key || seen[key]) return result
    seen[key] = true
    result.push(normalized)
    return result
  }, [])
}

export function resolveCompatibleTuple (candidates, savedResolution) {
  const allOptions = deduplicateConfigurationTuples(candidates)
  const compatible = allOptions.filter(candidate => candidate.canonical_match === true)
  const savedKey = savedResolution && savedResolution.technique && savedResolution.location
    ? (savedResolution.configuration_key || configurationTupleKey({
        program_id: savedResolution.ptl_program_id,
        product_class: savedResolution.selected_product_class,
        technique_id: savedResolution.technique && savedResolution.technique.technique_id,
        location_id: savedResolution.location && savedResolution.location.location_id
      }))
    : ''
  const preserved = savedKey ? allOptions.find(candidate => candidate.configuration_key === savedKey) : null
  if (preserved) return { status: 'resolved', source: savedResolution.resolution_source || 'automatic', selected: preserved, options: compatible, fallbackOptions: allOptions }
  if (savedKey) {
    return { status: 'selection_required', source: '', selected: null, options: compatible, fallbackOptions: allOptions, invalidated: true, previous: savedResolution }
  }
  if (compatible.length === 1 && !savedKey) return { status: 'resolved', source: 'automatic', selected: compatible[0], options: compatible, fallbackOptions: allOptions }
  return {
    status: compatible.length > 1 ? 'selection_required' : 'configuration_not_available',
    source: '',
    selected: null,
    options: compatible,
    fallbackOptions: allOptions,
    invalidated: !!savedKey,
    previous: savedKey ? savedResolution : null
  }
}

function debugTechnique (technique) {
  return technique ? {
    program_id: technique.program_id === undefined ? null : technique.program_id,
    technique_id: technique.technique_id === undefined ? null : technique.technique_id,
    technique_name: technique.technique_name || '',
    internal_id: technique.internal_id === undefined ? null : technique.internal_id,
    normalized_internal_id: normalizeCanonicalInternalId(technique.internal_id),
    serialized_internal_id: technique.serialized_internal_id === undefined ? technique.internal_id : technique.serialized_internal_id,
    hydration_source: technique.hydration_source || ''
  } : null
}

function debugLocation (location) {
  return location ? {
    location_id: location.location_id === undefined ? null : location.location_id,
    location_code: location.location_code || '',
    internal_id: location.internal_id === undefined ? null : location.internal_id,
    normalized_internal_id: normalizeCanonicalInternalId(location.internal_id),
    product_class: location.product_class || '',
    serialized_internal_id: location.serialized_internal_id === undefined ? location.internal_id : location.serialized_internal_id,
    hydration_source: location.hydration_source || ''
  } : null
}

export function buildDesignWorkspaces (designs, sections, sectionReady, lineUnits) {
  return (designs || []).map(design => {
    const designSections = (sections || []).filter(section => section.bundle_id === design.id)
    const lines = designSections.reduce((all, section) => all.concat(section.style_lines || []), [])
    const selectedLines = lines.filter(line => line.style_info && line.style_info.style_id)
    const programIds = designSections.reduce((ids, section) => {
      const id = section.program_info && section.program_info.program_id
      if (id && !ids.includes(String(id))) ids.push(String(id))
      return ids
    }, [])
    return {
      id: design.id,
      design,
      sections: designSections,
      styleCount: selectedLines.length,
      totalUnits: lines.reduce((total, line) => total + lineUnits(line), 0),
      programCount: programIds.length,
      ready: designSections.length > 0 && designSections.every(sectionReady),
      styleChips: selectedLines.map(line => ({
        label: (line.style_info.style_name || line.style_info.style_number || 'Style') + ' · ' + lineUnits(line)
      }))
    }
  })
}

export function initialActiveDesignId (workspaces) {
  const firstIncomplete = (workspaces || []).find(workspace => !workspace.ready)
  return firstIncomplete ? firstIncomplete.id : ((workspaces && workspaces[0]) ? workspaces[0].id : '')
}

export function buildSectionSummary (section, options) {
  const styleLines = section.style_lines || []
  const selectedLines = styleLines.filter(line => line.style_info && line.style_info.style_id)
  const placements = options.placements || []
  const totalUnits = styleLines.reduce((total, line) => total + (parseInt(line.units || 0, 10) || 0), 0)
  const colorCount = styleLines.reduce((total, line) => total + options.colorCount(line), 0)
  const ready = options.sectionReady(section)
  let missingReason = ''

  if (!section.program_info || !section.program_info.program_id) {
    missingReason = 'Select a program.'
  } else if (options.requiresDesign && !options.design) {
    missingReason = 'Assign Section decorations.'
  } else if (!selectedLines.length) {
    missingReason = 'Select at least one style.'
  } else if (styleLines.some(line => !line.style_info || !line.style_info.style_id)) {
    missingReason = 'Complete each style selection.'
  } else if (styleLines.some(line => !options.lineReady(line))) {
    missingReason = 'Add required color and quantity data.'
  } else if (!ready) {
    missingReason = 'Complete the assigned Section decorations.'
  }

  return {
    programName: section.program_info && section.program_info.program_name ? section.program_info.program_name : 'No program',
    designName: options.design && options.design.name ? options.design.name : 'No Section decorations',
    styleCount: selectedLines.length,
    totalUnits,
    colorCount,
    placementCount: placements.length,
    ready,
    missingReason,
    styleChips: selectedLines.map(line => ({
      label: (line.style_info.style_name || line.style_info.style_number || 'Style') + ' · ' + (parseInt(line.units || 0, 10) || 0)
    })),
    placementChips: placements.map(placement => {
      const technique = placement.technique && placement.technique.technique_name
        ? placement.technique.technique_name
        : 'Technique needed'
      const location = placement.location
        ? (placement.location.location_code || placement.location.location_name || 'Location needed')
        : 'Location needed'
      return { label: technique + ' @ ' + location }
    })
  }
}

export function toggleExpandedState (item) {
  item.expanded = !item.expanded
}
